import { Disclosure, DisclosureButton, DisclosurePanel } from '@headlessui/react';
import type { ReactNode } from 'react';

function Section({ title, children }: { title: string; children: ReactNode }) {
  return (
    <Disclosure as="div" className="border-b border-hilevel-border last:border-b-0">
      <h2>
        <DisclosureButton className="w-full text-left px-5 py-3 text-sm font-semibold text-hilevel-slate bg-hilevel-offwhite hover:bg-hilevel-light focus:outline-none focus:ring-2 focus:ring-inset focus:ring-hilevel-red/20 cursor-pointer border-0 transition-colors">
          {title}
        </DisclosureButton>
      </h2>
      <DisclosurePanel className="px-5 py-4 text-sm text-hilevel-slate space-y-3 leading-relaxed">
        {children}
      </DisclosurePanel>
    </Disclosure>
  );
}

export function TermsOfUsePage() {
  return (
    <div className="mx-auto max-w-5xl px-4 py-6 space-y-4">
      <div className="rounded-xl bg-hilevel-light p-8 sm:p-10">
        <h1 className="text-3xl sm:text-4xl font-bold tracking-tight text-hilevel-slate">
          Terms of Use
        </h1>
        <div className="mt-4 h-px bg-hilevel-border" />
        <p className="mt-4 text-sm text-hilevel-slate leading-relaxed">
          Welcome to the iQD Pro site. The Mezzanine Floor Group Ltd. and/or its affiliates provides
          the iQD Pro site to you subject to the following terms of use ("Site Terms").{' '}
          <b>By visiting the iQD Pro site, you accept the Site Terms.</b> Please read them
          carefully.
        </p>
      </div>

      <div className="rounded-xl border border-hilevel-border bg-white overflow-hidden">
        <Section title="Privacy">
          Please review our{' '}
          <a
            href="https://www.hi-levelmezzanines.co.uk/privacy-policy"
            target="_blank"
            rel="noreferrer"
          >
            Privacy Policy
          </a>
          , which also governs your visit to the iQD Pro site, to understand our practices.
        </Section>

        <Section title="Electronic Communications">
          When you visit the iQD Pro site or send e-mails to us, you are communicating with us
          electronically. You consent to receive communications from us electronically. We will
          communicate with you by e-mail or by posting notices on the iQD Pro site. You agree that
          all agreements, notices, disclosures and other communications that we provide to you
          electronically satisfy any legal requirement that such communications be in writing.
        </Section>

        <Section title="Copyright">
          All content included on the iQD Pro site, such as text, graphics, logos, button icons,
          images, audio clips, digital downloads, data compilations, ans software, is the property
          of The Mezzanine Floor Company or its content providers and protected by United Kingdom
          and international copyright laws. All software used on the iQD Pro site is the property of
          The Mezzanine Floor Group or its software suppliers and protected by United Kingdom and
          international copyright laws.
        </Section>

        <Section title="License and Site Access">
          <p>
            The Mezzanine Floor Group grants you a limited license to access and make personal use
            of the iQD Pro site and not to download (other than page caching) or modify it, or any
            portion of it, except with express written consent of The Mezzanine Floor Group. This
            license does not include any resale or commercial use of the iQD Pro site or its
            contents; any derivative use of the iQD Pro site or its contents; any downloading or
            copying of account information; or any use of data mining, robots, or similar data
            gathering and extraction tools. Unless otherwise specified by The Mezzanine Floor Group
            in a separate license, your right to use any software, data, documentation or other
            materials that you access or download through the iQD Pro site is subject to these site
            Terms or, if you have an iQD account, the Agreement.
          </p>
          <p>
            The iQD Pro site or any portion of the iQD Pro site may not be reproduced, duplicated,
            copied, sold, resold, visited, or otherwise exploited for any commercial purpose without
            express written consent of The Mezzanine Floor Group. You may not frame or utilize
            framing techniques to enclose any trademark, logo, or other proprietary information
            (including images, text, page layout, or form) of iQD Pro without express written
            consent. You may not use any meta tags or any other "hidden text" utilizing The
            Mezzanine Floor Group's name or trademarks without the express written consent of The
            Mezzanine Floor Group. Any unauthorized use terminates the permission or license granted
            by The Mezzanine Floor Group. You are granted a limited, revocable, and nonexclusive
            right to create a hyperlink to the home page of the iQD Pro site, so long as the link
            does not portray The Mezzanine Floor Group, or its products or services in a false,
            misleading, derogatory, or otherwise offensive matter. You may not use any iQD logo or
            other proprietary graphic or trademark as part of the link without express written
            permission.
          </p>
        </Section>

        <Section title="Your Account">
          If you use the iQD Pro site, you are responsible for maintaining the confidentiality of
          your iQD account and password and for restricting access to your computer, and you agree
          to accept responsibility for all activities that occur under your account or password. The
          Mezzanine Floor Group reserves the right to refuse service, terminate accounts, remove or
          edit content in its sole discretion.
        </Section>

        <Section title="Disclaimer of Warranties and Limitation of Liability">
          THE IQD PRO SITE AND ALL INFORMATION, CONTENT, MATERIALS, PRODUCTS (INCLUDING ANY
          SOFTWARE) AND SERVICES INCLUDED ON OR OTHERWISE MADE AVAILABLE TO YOU THROUGH THIS SITE
          ARE PROVIDED BY THE MEZZANINE FLOOR GROUP ON AN "AS IS" AND "AS AVAILABLE" BASIS, UNLESS
          OTHERWISE SPECIFIED IN THE AGREEMENT. THE MEZZANINE FLOOR GROUP MAKES NO REPRESENTATIONS
          OR WARRANTIES OF ANY KIND, EXPRESS OR IMPLIED, AS TO THE OPERATION OF THIS SITE OR THE
          INFORMATION, CONTENT, MATERIALS, PRODUCTS (INCLUDING ANY SOFTWARE) OR SERVICES INCLUDED ON
          OR OTHERWISE MADE AVAILABLE TO YOU THROUGH THE IQD PRO SITE, UNLESS OTHERWISE SPECIFIED IN
          WRITING. YOU EXPRESSLY AGREE THAT YOUR USE OF THIS SITE IS AT YOUR SOLE RISK. TO THE FULL
          EXTENT PERMISSIBLE BY APPLICABLE LAW, THE MEZZANINE FLOOR GROUP DISCLAIMS ALL WARRANTIES,
          EXPRESS OR IMPLIED, INCLUDING, BUT NOT LIMITED TO, IMPLIED WARRANTIES OF MERCHANTABILITY
          AND FITNESS FOR A PARTICULAR PURPOSE. THE MEZZANINE FLOOR GROUP DOES NOT WARRANT THAT THIS
          SITE; INFORMATION, CONTENT, MATERIALS, PRODUCTS (INCLUDING ANY SOFTWARE) OR SERVICES
          INCLUDED ON OR OTHERWISE MADE AVAILABLE TO YOU THROUGH THE IQD PRO SITE; ITS SERVERS; OR
          E-MAIL SENT FROM THE MEZZANINE FLOOR GROUP ARE FREE OF VIRUSES OR OTHER HARMFUL
          COMPONENTS. THE MEZZANINE FLOOR GROUP WILL NOT BE LIABLE FOR ANY DAMAGES OF ANY KIND
          ARISING FROM THE USE OF THE IQD PRO SITE OR FROM ANY INFORMATION, CONTENT, MATERIALS,
          PRODUCTS (INCLUDING SOFTWARE) OR SERVICES INCLUDED ON OR OTHERWISE MADE AVAILABLE TO YOU
          THROUGH THE IQD PRO SITE, INCLUDING, BUT NOT LIMITED TO DIRECT, INDIRECT, INCIDENTAL,
          PUNITIVE, AND CONSEQUENTIAL DAMAGES, UNLESS OTHERWISE SPECIFIED IN THE AGREEMENT.
        </Section>

        <Section title="Our Address">
          The Mezzanine Floor Group Ltd. <br />
          Unit 6, Petersfield Business Park <br />
          Bedford Road <br />
          Petersfield <br />
          Hampshire, GU32 3QA <br />
          United Kingdom
        </Section>
      </div>
    </div>
  );
}
